import {
  cfsToKcfs,
  findMinMax,
  getFlowUnits,
  getFloodCatUnits,
  getGaugeUnits,
  getMinMaxSeriesDataYaxis,
  getRatingForStage,
  isFalsy,
  isMissing,
  kcfsToCfs,
  kcfsToCfsString,
  ratingsLookup,
  seriesStyle,
  setSiteTimeZone,
  ucFirst,
} from '../../common/hydrographHelper.js'

import dayjs from 'dayjs'
import tz from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import adv from 'dayjs/plugin/advancedFormat'
import Highcharts from 'highcharts/highstock'

dayjs.extend(tz)
dayjs.extend(utc)
dayjs.extend(adv)

export function getGraphCreatedLabel(dateStr) {
  let year, day, hours, mins, ampm
  let mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  try {
    let d = dateStr ? dayjs(dateStr).valueOf() : dayjs().valueOf()
    // date
    year = d.getFullYear()
    let h = d.getHours()
    mon = mon[d.getMonth()]
    day = d.getDate()
    // time
    ampm = hours > 12 ? 'PM' : 'AM'
    let m = d.getMinutes()
    hours = h % 12
    hours = hours ? hours : 12
    mins = m < 10 ? `0${m}` : `${m}`
    return `Graph Created (${hours}:${mins}${ampm} ${mon} ${day}, ${year})`
  } catch (error) {
    console.error(`ERROR: `, error)
  }
}

// Get the ratings table data and arrange it for yAxis1 (Stage) and yAxis2 (Flow).
export function getTickPositionerData(ratingsCurve, series) {
  if (!ratingsCurve || !ratingsCurve.length || !series || series.observed.primaryUnits != 'ft') return []
  let chartDomain = series.extremes.yaxis
  let arr1, arr2
  // If we have more than 25 ratings values then just grab the whole values
  if (ratingsCurve.length > 25) {
    let stages = ratingsCurve.filter(function (i) {
      if (i.Stage % 1 === 0 && i.Stage <= chartDomain[0].max + 5 && i.Stage >= chartDomain[0].min - 5) {
        return i
      } else {
        return false
      }
    })
    arr1 = stages.map((i) => i.Stage)
    arr2 = stages.map((i) => i.Flow)
  } else {
    arr1 = ratingsCurve.map((i) => i.Stage)
    arr2 = ratingsCurve.map((i) => i.Flow)
  }
  return {
    y1: arr1,
    y2: arr2,
  }
}

// Get the xaxis min, max values
export function getXAxisExtremes(series, gauge, back, ahead) {
  if (series.dataExists === false || !gauge) return { min: null, max: null, minString: '', maxString: '' }
  let min = dayjs().subtract(back, 'days')
  let max = dayjs().add(ahead, 'days')
  let xaxis = {
    min: min.valueOf(),
    max: max.valueOf(),
    minString: `${min.toISOString()}`,
    maxString: `${max.toISOString()}`,
  }
  return xaxis
}

// This is the horizontal line for the record stage value
// TODO: further review is needed
export function getRecordStageLine(series, gauge) {
  if (!series.floodcats || !gauge.flood || !series.crests || !gauge) return (series['record_stageline'] = null)
  let units = series.floodcats.stageUnits == 'ft' ? 'stage' : 'flow'

  let record = gauge.flood.crests.historic.filter((item) => {
    return item.preliminary === 'R'
  })[0]
  let val = record ? record[units] : null
  let unitType = { stage: 'ft', flow: 'cfs' }
  if (!val || !series.observed || !series.observed.length) return []
  let unitSymbol = ''
  if (unitType[units] === 'ft') {
    unitSymbol = `'`
  }
  return (series['record_stageline'] = {
    name: seriesStyle['record_stageline'].name,
    color: seriesStyle['record_stageline'].color,
    width: 2,
    zIndex: 5,
    value: Number(val),
    yaxis: 0,
    dashStyle: 'dash',
    id: 'line_record',
    label: {
      zIndex: 5,
      text: `Record Flood Level: ${val}${unitSymbol}`,
      align: 'center',
      x: -5,
      style: {
        color: seriesStyle['record_stageline'].color,
        fontWeight: 'bold',
        fontStyle: 'italic',
      },
    },
  })
}
/**
 * Get whole numbers from rawData.
 * @param {*} rawData
 * @returns array of objects
 */
export function getWholeNumberTicks(rawData) {
  if (rawData.length == 0) return []
  let ticks = []
  for (let i = 0; i < rawData.length - 1; i++) {
    let r = rawData[i].primary
    if (isFalsy(r)) continue
    ticks.push(r)
  }
  // If we have a single value this causes
  // "Infinity" issues.
  if (ticks.length && ticks.length == 1) {
    let v = ticks[0]
    let top = v + 1
    let bot = v - 1
    ticks = [...ticks, top, bot]
  }
  return ticks.length ? ticks.sort((a, b) => a - b) : []
}
// The record stageline toggle config for the legend
export function getRecordStageLineConfig(series) {
  if (!series || !series.observed || series.observed === null || !series.observed.length)
    return (series['record_stageline_toggle'] = null)
  let val = series.record_stageline.value
  // We still want to display the record level in the legend but not have it scale the hydrographs. TM
  if (series.record_stageline && !isFalsy(val)) {
    return (series['record_stageline_toggle'] = {
      name: series.record_stageline.label.text,
      id: series.record_stageline.id,
      color: seriesStyle['record_stageline'].color,
      marker: seriesStyle['record_stageline'].marker,
    })
  }
  return (series['record_stageline_toggle'] = null)
}

// @TODO: currently NOT USED....
// Binary, recursive search pattern
export function findRatings(value, block, type) {
  var oppType = { stage: 'flow', flow: 'stage' }
  let mid = Math.floor(block.length / 2)
  if (block.length === 1 && parseFloat(block[0][type]).toFixed(2) != parseFloat(value).toFixed(2)) return false
  if (
    parseFloat(block[mid][type]).toFixed(2) >= parseFloat(value).toFixed(2) &&
    parseFloat(block[mid + 1][type]).toFixed(2) < parseFloat(value).toFixed(2)
  ) {
    let ret = block[mid][oppType[type]] // return true...it was found here!
    return ret
  } else if (parseFloat(value).toFixed(2) < parseFloat(block[mid][type]).toFixed(2)) {
    for (let i = 0; i < block[mid].length; i++) {
      if (
        parseFloat(block[i][type]).toFixed(2) >= parseFloat(value).toFixed(2) &&
        parseFloat(block[i + 1][type]).toFixed(2) < parseFloat(value).toFixed(2)
      ) {
        let ret = block[i][oppType[type]] // return true...it was found here!
        return ret
      }
    }
    return findRatings(value, block.slice(mid), type)
  } else if (parseFloat(value).toFixed(2) > parseFloat(block[mid][type]).toFixed(2)) {
    for (let i = 0; i < block[mid].length; i++) {
      if (
        parseFloat(block[i][type]).toFixed(2) >= parseFloat(value).toFixed(2) &&
        parseFloat(block[i + 1][type]).toFixed(2) < parseFloat(value).toFixed(2)
      ) {
        let ret = block[i][oppType[type]] // return true...it was found here!
        return ret
      }
    }
    return findRatings(value, block.slice(0, mid), type)
  }
}

// Parse the streamflows to add to the series object
export function parseStreamFlow(series, curve, json, streamflow) {
  if (!series || !json) return []
  let d,
    stages = [],
    flows = []
  let unitStringTranslation = { ft: 'ft', 'ft^3/sec': 'cfs' }
  let arr = [],
    result
  for (let i = 0; i <= json[0].data.length - 1; i++) {
    let obj = json[0].data,
      val = json[0].data[i].value
    d = dayjs(obj[i]['forecast-time']).valueOf()
    if (isMissing(val)) continue
    if (curve === null) {
      arr.push({ stage: val, flow: val, valid: d.getTime(), validString: d })
      stages.push(val)
      flows.push(val)
    } else {
      switch (json[0].units) {
        case 'ft':
          result = ratingsLookup(curve, val, 'stage', series) // should return flow value
          arr.push({ stage: val, flow: result, valid: d.getTime(), validString: d })
          stages.push(val)
          flows.push(result)
          break
        case 'ft^3/sec':
          // NOTE: this should always be CFS flow data; no conversion needed.
          result = ratingsLookup(curve, val, 'flow', series) // should return stage value
          arr.push({ stage: result, flow: val, valid: d.getTime(), validString: d })
          stages.push(result)
          flows.push(val)
          break
        default:
          // no units given for this streamflow
          //console.error(`streamflow '${streamflow}' does not have unit type: ${json[0].unit}`)
          arr.push({ stage: val, flow: null, valid: d.getTime(), validString: d })
          stages.push(val)
          break
      }
    }
  }
  json[0].id = streamflow
  let max0 = stages.length ? Math.max.apply(Math, stages) : -9999
  let min0 = stages.length ? Math.min.apply(Math, stages) : -9999
  let max1 = flows.length ? Math.max.apply(Math, flows) : -9999
  let min1 = flows.length ? Math.min.apply(Math, flows) : -9999
  return {
    valid: json[0]['reference-time'],
    data: arr,
    units: json[0].units,
    unitString: unitStringTranslation[json[0].units],
    stage_max: max0,
    stage_min: min0,
    flow_max: max1,
    flow_min: min1,
  }
}

// Get gauge information
// NOTE: this function stays in this file due to dayjs implementation
export function getGaugeData(series, gauge, current) {
  // Set the timezone on the whole chart not the series.gauge.
  // TODO: move this out of here into its own method; TZ should not be linked to a gauge. TM
  series.timeZone = setSiteTimeZone(gauge ? gauge.timeZone : 'UTC')
  series.timeZoneAbbr = dayjs().tz(series.timeZone).format('z')

  if (!gauge || !current) return { dataExists: false }
  let ret = {
    lid: gauge.lid.toLowerCase(),
    datum: gauge.datum,
    reachId: gauge.reachId,
    usgsId: gauge.usgsId,
    name: gauge.name,
    county: gauge.county,
    lat: gauge.latitude,
    lon: gauge.longitude,
    rfc: gauge.rfc,
    elev: gauge.elevation,
    status: gauge.status,
    pedts: gauge.pedts,
    created: current,
  }
  return ret
}

export function getLastUpdated(val) {
  return dayjs(val).toISOString()
}

// Returns the gauge timezone value
export function getGaugeTimeZone(series) {
  if (!series.timeZone) return ''
  return series.timeZone
}

// Setup the links in the custom legend
export function getLegendSections(chartSeries, type) {
  return chartSeries.filter((i) => type.includes(i.userOptions.id))
}

// Observed gradient background color for half the chart
export function updateObservedGradient(chart, opts, currentDateTime) {
  // Provide a change in background shading between historic and future
  let xAxis = chart.xAxis[0]
  opts.chart.plotBackgroundColor.stops[0][0] = 0
  opts.chart.plotBackgroundColor.stops[1][0] =
    (chart.plotWidth - chart.chartWidth + 10 + xAxis.toPixels(currentDateTime, false)) / chart.plotWidth
  opts.chart.plotBackgroundColor.stops[2][0] =
    (chart.plotWidth - chart.chartWidth + 10 + xAxis.toPixels(currentDateTime, false)) / chart.plotWidth
  opts.chart.plotBackgroundColor.stops[3][0] = 1
  chart.update({
    chart: opts,
  })
}

// Format the Graph Created and Forecast Issues timestamp
export function getDateTimeStamp(series, type) {
  let ts

  if (!series || !type) {
    return ts
  }

  switch (type) {
    case 'now':
      // If we want current time then empty the ts var.
      ts = undefined
      break
    case 'fcst':
      // Not all gauges have a forecast so this is a bit hacky and a "best effort" to display the timestamp. TM
      try {
        if (series.forecastIssuedTime) {
          ts = series.forecastIssuedTime
        }
      } catch {
        // If no forecast timestamp
        return 'N/A'
      }
      break
  }

  try {
    return dayjs(ts).tz(series.timeZone).format('hh:mm A z MMM D YYYY')
  } catch (e) {
    console.error(`Could not convert timezone '${ts}' to '${series.timeZone}': `, e)
    return 'N/A'
  }
}

// Create the OBSERVED-FORECAST topbar
export function obsForecastTopBar(obj, series) {
  if (!obj.hasData() || !series || !series.version) return
  let nowP = obj.xAxis[0].toPixels(dayjs().valueOf())
  let extX = obj.xAxis[0].getExtremes()
  // HydrographNWM uses the secondary yAxis as the primary.
  let extY = obj.yAxis[1].getExtremes()
  let xmaxP = obj.xAxis[0].toPixels(extX.max)
  let xminP = obj.xAxis[0].toPixels(extX.min)
  // HydrographNWM uses the secondary yAxis as the primary.
  let ymaxP = obj.yAxis[1].toPixels(extY.max)

  if (nowP > xminP) {
    obj.observedLine = obj.renderer
      .path(['M', xminP + 10, ymaxP - 20, 'L', nowP - 10, ymaxP - 20])
      .attr({
        'stroke-width': 3,
        stroke: '#5e85c0', // Per Nature Nurture graphic. TM
        class: 'nwsRenderNWM',
        zIndex: 3,
      })
      .add()
    obj.observedLabel = obj.renderer
      .label('PAST', (nowP + xminP) / 2, ymaxP - 30)
      .attr({
        fill: series.version.chart === 'nwm' ? '#DAEAF6' : '#FFFFFF',
        padding: 1,
        zIndex: 4,
        class: 'nwsRenderNWM',
        align: 'center',
      })
      .css({
        color: '#5e85c0', // Per Nature Nurture graphic. TM
        fontSize: '16px',
        fontStyle: 'italic',
      })
      .add()
  }

  if (nowP <= xminP) {
    nowP = obj.xAxis[0].toPixels()
  }

  if (nowP < xmaxP) {
    obj.forecastLine = obj.renderer
      .path(['M', nowP + 10, ymaxP - 20, 'L', xmaxP - 10, ymaxP - 20])
      .attr({
        'stroke-width': 3,
        stroke: '#8c6bb0', // Per Nature Nurture graphic. TM
        class: 'nwsRenderNWM',
        zIndex: 3,
      })
      .add()
    obj.forecastLabel = obj.renderer
      .label('FUTURE', (nowP + xmaxP) / 2, ymaxP - 30)
      .attr({
        fill: '#DAEAF6',
        padding: 1,
        zIndex: 4,
        class: 'nwsRenderNWM',
        align: 'center',
      })
      .css({
        color: '#8c6bb0', // Per Nature Nurture graphic. TM
        fontSize: '16px',
        fontStyle: 'italic',
      })
      .add()
  }
}

// "Latest observed value" message box on chart in upper left corner
export function getLatestObservedValues_OLD(series) {
  if (
    !series ||
    !series.observed ||
    !series.observed.data ||
    !series.observed_toggle.data ||
    !series.observed_toggle.data.length
  )
    return false
  try {
    let obsValue = series.observed ? series.observed_toggle.data[series.observed_toggle.data.length - 1].y : null
    let obsUnits = series.observed ? kcfsToCfsString(series.observed.primaryUnits) : null
    let stageOrFlow = series.observed && series.observed.primaryName == 'Stage' ? 'stage' : 'flow'
    let v = series.floodcats.data[stageOrFlow].minor
    let unitsType = series.observed.primaryUnits
    let floodValue = v ? (stageOrFlow === 'flow' ? cfsToKcfs(v) : v) : null
    let o = dayjs(series.observed_toggle.data[series.observed_toggle.data.length - 1].x).tz(series.timeZone)
    if (!o) return false
    let msg = `Latest observed value: ${obsValue} ${obsUnits}<br>at ${o.format('h')}:${o.format('mm')} ${o.format(
      'A'
    )} ${o.format('z')} ${o.format('D')}-${o.format('MMM')}-${o.format('YYYY')}. `
    if (floodValue) {
      msg += `<span style="color:#000000; font-weight:bold;">Flood ${ucFirst(
        stageOrFlow
      )} is ${floodValue} ${unitsType}</span>`
    }
    return msg
  } catch (err) {
    console.error(`ERROR with getLatestObservedValues with value :${series.timeZone}: `, err)
  }
}

export function checkYaxisTitle(val, obsOrFcst) {
  if (!val) return ''
  let ret
  if (obsOrFcst === 'observed') {
    ret = 'Flow'
    if (val.toLowerCase() === 'stage') {
      ret = 'River Level'
    }
  } else {
    ret = 'Stage'
    if (val.toLowerCase() !== 'stage') {
      ret = 'Flow'
    }
  }
  return ret
}

export function onlyUnique(value, index, self) {
  return self.indexOf(value) === index
}

export function verifyDataExists(gauge, reachID) {
  if (reachID || gauge) return true
  return false
}

// Very fast search pattern
export function searchRatingsCurve(curve, value, start, end) {
  if (value <= curve[start].Stage) return value // return the Stage value here
  if (value >= curve[end].Stage) return curve[end].Flow
  if (start > end) return false
  let mid = Math.floor((start + end) / 2)
  if (value === curve[mid].Stage || (value > curve[mid].Stage && value <= curve[mid + 1].Stage)) return curve[mid].Flow
  if (curve[mid].Stage > value) {
    return searchRatingsCurve(curve, value, start, mid - 1)
  } else {
    return searchRatingsCurve(curve, value, mid + 1, end)
  }
}

export function updateSecondaryYAxis(obsSorted, series, val) {
  if (!series.ratingsCurve) return val
  // The if statement deals with obscure gauges like BIGI1
  if (series.observed.primaryUnits != 'ft') {
    if (val <= obsSorted[0].stage) return obsSorted[0].flow
    if (val >= obsSorted[obsSorted.length - 1].stage) return obsSorted[obsSorted.length - 1].flow
    let found = obsSorted.find((i) => i.stage === val)
    return found ? found.flow : null
  }
  return searchRatingsCurve(series.ratingsCurve, val, 0, series.ratingsCurve.length - 1).toFixed()
}

// Hydrograph updates after chart load
export function adjustXaxisHydrographNWM(chart, series) {
  if (!series || !chart) return
  let ex = chart.xAxis[0].getExtremes()

  // @TODO: verify this is the case: MRM is maximum visible forecast on load all the time?
  let visibleDataMax = series.nwm['medium_range_ensemble_mean'].xmax
  chart.xAxis[0].setExtremes(ex.dataMin, visibleDataMax)
}

// @TODO: Example for BIGI1 (The problem child) where we have large amount of
// chart space above the data.
export function adjustVisualData(chart, series) {
  if (!series || !series.extremes || !series.extremes.xaxis) return
  let now = dayjs()

  // Set the "7d" (7 Day) zoom view setting or less than 7 days xaxis extremes
  const minDate = dayjs(series.extremes.xaxis.min)
  if (minDate.diff(series.extremes.xaxis.max, '7 days')) {
    chart.xAxis[0].setExtremes(dayjs(series.extremes.xaxis.min).valueOf(), dayjs(series.extremes.xaxis.max).valueOf())
  } else {
    chart.xAxis[0].setExtremes(now.subtract(84, 'hour').valueOf(), now.add(84, 'hour').valueOf())
  }
}

// For the "Reformat Gauge Page" in OWP-795
// This render displays metadata under the "Site Time" on the chart
export function getMetaDataSubHeading(chart, series, gaugeConfig) {
  if (series.dataExists == false || !series.gauge || !series.gauge.datum) return
  let attributions = gaugeConfig ? gaugeConfig.dataAttribution : null
  let datumElev = isMissing(series.gauge.datum.elevation) ? 'N/A' : series.gauge.datum.elevation
  let datumOption =
    gaugeConfig && gaugeConfig.datums.vertical.value.length
      ? gaugeConfig.datums.vertical.options.filter((i) => i.id === gaugeConfig.datums.vertical.value[0].id)
      : null
  let datumOpt = datumOption ? datumOption[0].abbrev.replace('*', '') : 'N/A'
  let attrTextArr = []

  // Responsive placement of metadata labels
  let extX = chart.xAxis[0].getExtremes()
  let xmaxP = chart.xAxis[0].toPixels(extX.max)
  let extY = chart.yAxis[0].getExtremes()
  let xminP = chart.xAxis[0].toPixels(extX.min)

  // Display at most top two data attribution links on the hydrograph. (TM)
  if (attributions && attributions.value.length > 0) {
    attributions.value.slice(0, 2).forEach((a) => {
      attrTextArr.push(`<a href="${a.url.value}" target="_blank">${a.title.value}</a>`)
    })
  }

  // TODO: If no attribution text, then no need to create label. (TM)
  let labelAttribution = chart.renderer
    .label(attrTextArr.join('<br/>'), null, null, null, null, null, true)
    .css({
      width: '600px',
      border: '0',
      fontSize: '10px',
    })
    .attr({
      class: 'metadataLabelNWM',
      padding: 5,
    })
    .add()

  let labelLidCSS = {
    width: '600px',
    border: '0',
    fontSize: '10px',
  }

  // Prevent labelLid text overlap.
  labelLidCSS.width = '260px'
  if (xmaxP < 800) {
    labelLidCSS.width = '160px'
  }

  // Character at end of string literal template is a degree symbol (ascii: &#730;)
  let labelLid = chart.renderer
    .label(
      `${series.gauge.lid.toUpperCase()} (plotting  ${series.gauge.pedts.observed.toUpperCase()}) "Gage 0" Datum (${datumOpt}): ${datumElev}` +
        '&#39;'
    )
    .css(labelLidCSS)
    .attr({
      class: 'metadataLabelNWM',
      padding: 5,
    })
    .add()

  // Hide the "Forecast Issued" text if the PEDTs is old (expired).
  let PEDTSExpired = false
  let labelForecastIssuedStr = ` - Forecast Issued (${getDateTimeStamp(series, 'fcst')})`

  if (series.gauge.status.forecast == null || series.gauge.status.forecast.floodCategory == 'old') {
    PEDTSExpired = true
  } else if (
    series.gauge.status.forecast.floodCategory == '' ||
    series.gauge.status.forecast.floodCategory == 'unknown'
  ) {
    if (
      series.gauge.status.forecast != null &&
      series.gauge.status.forecast.primary <= -999 &&
      series.gauge.status.forecast.secondary <= -999
    ) {
      PEDTSExpired = true
    }
  }

  if (PEDTSExpired || series.forecastIssuedTime == '0001-01-01T00:00:00Z') {
    labelForecastIssuedStr = ''
  }

  let labelTime = chart.renderer
    .label(`Graph Created: (${getDateTimeStamp(series, 'now')})${labelForecastIssuedStr}`)
    .css({
      width: '600px',
      border: '0',
      fontSize: '10px',
    })
    .attr({
      class: 'metadataLabelNWM',
      padding: 5,
    })
    .add()

  // Default placement settings
  let labelLid_x = 350
  let labelLid_y = -110
  let labelAttribution_x = -385
  let labelAttribution_y = -110
  let labelTime_x = -10
  let labelTime_y = -90

  // National map view popout settings
  // SPOW1 - chart.plotHeight: 305 perfect
  // BIGI1 - chart.plotHeight: 336 higher
  //console.log(`chart totals: `, parseFloat(chart.plotHeight + chart.marginBottom), `, xmaxP :${xmaxP}:, chart.plotHeight: `, chart.plotHeight, `, chart.marginBottom: `, chart.marginBottom)
  if (xmaxP <= 900) {
    // For Window's Chrome browser: prevent
    // from mashing into "Site Time".
    //labelLid_x = 180
    labelLid_x = 190
    labelAttribution_x = -220

    // Prevent labelLid from being cut off.
    if (xmaxP < 480) {
      // labelLidCSS.width = '130px'
      labelLid_x = 150
      labelAttribution_x = -180
    }

    // Hack
    if (chart.plotHeight == 305) {
      labelLid_y = -144
      labelAttribution_y = -144
      labelTime_y = -122
    } else if (chart.plotHeight == 336) {
      labelAttribution_y = -110
      labelLid_y = -110
      labelTime_y = -90

      if (series.forecast && !series.forecast.length) {
        labelLid_x = 210
        labelAttribution_x = -185
      }
    }

    // On the National Map (index view) adjust
    // so the "Site Time" in not overlapping.
    if (series.version.view === 'mapgauge') {
      labelAttribution_y = -105
      labelLid_y = -105
      labelLid_x = 172
    }
  }

  // Push labelLid down when minimizing browser on gauge view.
  if (xmaxP < 800 && series.version.view !== 'mapgauge') {
    labelLid_y = -100
    labelLid_x = 190
  }

  //console.log(`xminP :${xminP}:, xmaxP :${xmaxP}:, labelAttribution_x :${labelAttribution_x}:, labelLid_y: ${labelLid_y}:, labelLid_x: ${labelLid_x}:, extX: `, extX)

  labelAttribution.align(
    Highcharts.extend(labelAttribution.getBBox(), {
      align: 'center',
      x: labelAttribution_x,
      verticalAlign: 'bottom',
      y: labelAttribution_y,
    }),
    null,
    'spacingBox'
  )

  labelLid.align(
    Highcharts.extend(labelLid.getBBox(), {
      align: 'center',
      x: labelLid_x,
      verticalAlign: 'bottom',
      y: labelLid_y,
    }),
    null,
    'spacingBox'
  )

  labelTime.align(
    Highcharts.extend(labelTime.getBBox(), {
      align: 'center',
      x: labelTime_x,
      verticalAlign: 'bottom',
      y: labelTime_y,
    }),
    null,
    'spacingBox'
  )
}

/**
 * Setup the title for yAxis
 * @param object stageflow
 * @param stringobsOrFcst
 * @returns string
 */
export function getYaxisText(series, stageflow, obsOrFcst) {
  let title = '',
    unit = '',
    value = ''
  if (
    series &&
    series.version.chartName === 'nwm' &&
    !stageflow.observed &&
    !stageflow.forecast &&
    obsOrFcst === 'observed'
  )
    return 'Flow (cfs)'

  if (!stageflow || !stageflow[obsOrFcst] || (obsOrFcst === 'forecast' && !series.ratings)) return ''
  if (obsOrFcst === 'forecast') {
    title = stageflow[obsOrFcst].secondaryName
    unit = kcfsToCfsString(stageflow[obsOrFcst].secondaryUnits)
    return `${title} (${unit})`
  }
  if (obsOrFcst === 'observed') {
    title = stageflow[obsOrFcst].primaryName
    unit = kcfsToCfsString(stageflow[obsOrFcst].primaryUnits)
    return `${title} (${unit})`
  }
  return value
}

/**
 * Latest observed value" message box on chart in upper left corner
 * @param object series
 * @param object gaugeConfig
 * @returns string msg
 */
export function getLatestObservedValues(series, gaugeConfig) {
  if (
    !series ||
    !gaugeConfig ||
    !series.y0Enabled ||
    !series.y0.seriesData.observed ||
    !series.y0.seriesData.observed.length
  )
    return

  let val = gaugeConfig.floodCatUnits.options.filter((i) => i.id === gaugeConfig.floodCatUnits.value)
  let floodUnit = val && val.length ? val[0].value : null
  let stageOrFlow = series.y0.units === 'ft' ? 'stage' : 'flow'
  let floodValue = gaugeConfig.flood.categories.minor[stageOrFlow].value

  let obsData = series.y0.seriesData.observed
  let obsValue =
    obsData instanceof Array && obsData.length && obsData[0].length ? obsData[obsData.length - 1][1] : false
  let obsTime = obsData instanceof Array && obsData.length && obsData[0].length ? obsData[obsData.length - 1][0] : false
  let obsUnit = kcfsToCfsString(series.y1.units, gaugeConfig && gaugeConfig.hydrographNWM)
  let o = dayjs(obsTime).tz(series.timeZone)
  let msg = `Latest observed value: ${obsValue} ${obsUnit}<br>${o.format('h')}:${o.format('mm')} ${o.format(
    'A'
  )} ${o.format('z')} ${o.format('D')}-${o.format('MMM')}-${o.format('YYYY')}`

  if (!obsValue && !obsTime && o !== '') return false

  if (!isFalsy(floodValue) && floodUnit && series.y0Enabled) {
    msg += `<br><span style="color:#000000; font-weight:bold;">Flood ${ucFirst(
      stageOrFlow
    )} is ${floodValue} ${floodUnit}</span>`
  }

  return msg
}

/**
 * Show the colored stageflow bands on chart.
 * This function is proprietary to the Official Hydrograph
 * in that it creates STAGE (FT) based data points.
 * This function is specific to NWM Hydrograph due to the secondary yAxis.
 * @param {*} series
 * @returns
 */
export function getFloodLevelsConfig(series, gauge, stageflow) {
  // We have to have ratings because the control is on the secondary yAxis.
  if (!series || !gauge || !series.floodcategories || (stageflow.observed && series.y0.units !== 'ft')) return null

  let rets = []
  let upper = 0
  // TODO: TEMP TEST for low flood cat, gauge MSPM7
  if (gauge.lid == 'MSPM7') {
    series.floodcategories['low'] = {}
    series.floodcategories['low']['stage'] = 1.3
    series.floodcategories['low']['flow'] = { cfs: 70, kcfs: 0.007 }
  }
  for (let [k, v] of Object.entries(series.floodcategories)) {
    let flowVal = v.flow[series.flowUnitsByCMS]
    if (isMissing(flowVal)) continue
    if (k === 'low' && series.extremes.yaxis && series.extremes.yaxis.length) {
      upper = series.extremes.yaxis[0].min
    }
    if (parseFloat(flowVal) === parseFloat(upper)) continue // skip categories that are the same.
    if (flowVal != upper && !isMissing(flowVal) && !isMissing(upper)) {
      rets.push({
        name: ucFirst(k),
        class: 'plotbands',
        color: k === 'minor' ? seriesStyle['flood'].color : seriesStyle[k].color,
        from: flowVal,
        to: parseFloat(upper),
        zIndex: 0,
        yAxis: series.version.chart === 'nwm' ? 1 : 0,
        id: `band_${k}`,
      })
      upper = flowVal
    }
  }
  if (rets && rets.length) {
    // This should create the top level for the last flood category going over the chart border.
    const totalExts = getMinMaxSeriesDataYaxis(series.y0.seriesData)
    if (
      series.record_stageline &&
      series.record_stageline.value > rets[0].from &&
      series.record_stageline.value > totalExts[1]
    ) {
      rets[0].to = Math.ceil(series.record_stageline.value) + series.headRoom + 1
    } else if (totalExts[1] > rets[0].from) {
      rets[0].to = Math.ceil(totalExts[1]) + series.headRoom + 1
    } else {
      rets[0].to = Math.ceil(rets[0].from) + series.headRoom + 1
    }
  }
  return rets
}

/**
 * Show the stageflow lines with labels.
 * This function is proprietary to the Official hydrograph
 * in that it only shows STAGE data in "ft".
 * @param {*} series
 * @param {*} gauge
 * @returns object config
 */
export function getGaugeStageFlowLinesLabels(series, gaugeConfig) {
  if (!series || !series.floodcategories || !series.plotbands) return null
  // Using NWSCMS showing FT, KCFS or CFS for "Display Flood Categories As". Default is FT.
  let units = getFloodCatUnits(gaugeConfig)
  let rets = []
  for (let [k, v] of Object.entries(series.floodcategories)) {
    let val = units != 'ft' ? v.flow[units] : v.stage
    let stageString = ucFirst(k)
    let value = series.plotbands.find((i) => i.name === stageString)
    if (!value) continue
    rets.push({
      id: `band_label_${k.toLowerCase()}`,
      color: 'black',
      class: 'plotbands',
      width: 1,
      zIndex: 3,
      value: value.from,
      label: {
        text: !isMissing(val)
          ? `${stageString}: ${val} ${units}`
          : `${stageString}: Out of ${units} range [${value.from} ft]`,
        textAlign: 'left',
        fontWeight: 'bold',
        y: -2,
      },
    })
  }
  return rets
}

/**
 * Show the colored stageflow bands on chart
 * This function is proprietary to the NWM Hydrograph
 * in that it creates FLOW (CFS/KCFS) data points.
 * @param {*} series
 * @returns array
 */
export function getFloodLevelsConfigOLD(series, gauge, gaugeConfig, stageflow) {
  // We have to have ratings because the control is on the secondary yAxis.
  if (
    !series ||
    !series.ratings ||
    !gauge ||
    (stageflow && stageflow.observed && stageflow.observed.primaryUnits && stageflow.observed.primaryUnits !== 'ft')
  )
    return null

  let rets = [],
    upper = 0,
    val
  let isKcfs = getFlowUnits(gaugeConfig, 'hydrographNWM') === 'kcfs' ? true : false
  // TODO: TEMP TEST for low flood cat, gauge MSPM7
  if (gauge.lid == 'MSPM7') {
    series.floodcats.data['stage']['low'] = 1.3
  }
  for (let [k, v] of Object.entries(gauge.flood.categories)) {
    upper = k === 'low' ? series.extremes.yaxis[1].min : upper
    val = ratingsLookup(series.ratings, v.stage, 'stage')
    val = isKcfs ? cfsToKcfs(val) : val
    if (val != upper && !isMissing(val)) {
      rets.push({
        name: ucFirst(k),
        class: 'plotbands',
        color: k === 'minor' ? seriesStyle['flood'].color : seriesStyle[k].color,
        from: val,
        to: upper,
        zIndex: 0,
        yAxis: 1,
        id: `band_${k}`,
      })
      upper = val
    }
  }
  if (rets && rets.length) {
    // This should create the top level for the last flood category going over the chart border.
    rets[0].to = rets[0].from + series.extremes.yaxis[1].max + 1
  }
  return rets
}

/**
 * Show the stageflow lines with labels.
 * @param {*} series
 * @param {*} gauge
 * @returns object config
 */
export function getGaugeStageFlowLinesLabelsOLD(series, gaugeConfig) {
  if (!series || !series.plotbands || !series.plotbands.length) return null
  // Using NWSCMS showing FT, KCFS or CFS for "Display Flood Categories As". Default is FT.
  let d = gaugeConfig ? gaugeConfig.floodCatUnits.options.filter((i) => i.id === gaugeConfig.floodCatUnits.value) : null
  let units = d && d.length ? d[0].value : null
  let rets = []
  series.plotbands.forEach((v) => {
    // Default is CFS
    let val = v.from
    // We want KCFS based on NWSCMS config
    if (units === 'cfs' && series.y1.units === 'kcfs') val = kcfsToCfs(v.from)
    // We want KCFS based on NWSCMS config
    if (units === 'kcfs' && series.y1.units === 'cfs') val = cfsToKcfs(v.from)
    // We want FT based on NWSCMS config
    if (units === 'ft') val = ratingsLookup(series.ratings, v.from, 'flow', series.y1.units)
    rets.push({
      id: `band_label_${v.name.toLowerCase()}`,
      color: 'black',
      class: 'plotbands',
      width: 1,
      zIndex: 3,
      value: v.from,
      label: {
        text: `${v.name}: ${val} ${units}`,
        textAlign: 'left',
        fontWeight: 'bold',
        y: -2,
      },
    })
  })
  return rets
}

// Helper (this file) function
export function parseGaugeData(gauge) {
  let data = {},
    stages = [],
    flows = []
  if (!gauge || !gauge.flood || !gauge.flood.crests || !gauge.flood.crests.historic)
    return {
      data: [],
      stageUnits: null,
      flowUnits: null,
      stage_max: null,
      stage_min: null,
      flow_max: null,
      flow_min: null,
    }
  let units = getGaugeUnits(gauge.flood.crests)
  for (let [k, v] of Object.entries(gauge.flood.crests.historic)) {
    if (isMissing(v.stage)) continue // @TODO: this may not be needed?
    // Make sure we convert KCFS to CFS if that is what we have here
    let sval = units.stage == 'kcfs' ? kcfsToCfs(v.stage) : v.stage
    let fval = units.flow == 'kcfs' ? kcfsToCfs(v.flow) : v.flow
    let d = dayjs(v.occurredTime).valueOf()
    if (!(d in data)) data[d] = []
    data[d] = {
      stage: sval,
      flow: fval,
    }
    stages.push(sval)
    flows.push(fval)
  }
  // FAST!!
  let stageMinMax = findMinMax(stages)
  let flowMinMax = findMinMax(flows)
  return {
    data: data,
    stageUnits: units.stage,
    flowUnits: units.flow,
    stage_min: stageMinMax[0],
    stage_max: stageMinMax[1],
    flow_min: flowMinMax[0],
    flow_max: flowMinMax[1],
  }
}

/**
 * Parse out the crests data to match existing series data structure
 * @param object series
 * @param object gauge
 * @returns null | object
 */
export function parseCrestsData(series, gauge) {
  if (!gauge || !gauge.flood || !gauge.flood.crests) return (series['crests'] = null)
  return { ...series.crests, ...parseGaugeData(gauge) }
}

/**
 * Parse out the low water data to match existing series data structure
 * @param object series
 * @param object gauge
 * @returns object
 */
export function parseLowWaterData(gauge) {
  if (!gauge || !gauge.flood || !gauge.flood.lowWaters)
    return {
      0: { max: null, min: null, dataMax: null, dataMin: null },
      1: { max: null, min: null, dataMax: null, dataMin: null },
    }
  return parseGaugeData(gauge)
}

/**
 * This is used with parse stageflows and streamflows; this is does our heavy-lifting...
 * @param object json
 * @param object ratings
 * @returns object
 */
export function getNwmData(json, ratings) {
  try {
    let datums = []
    let flowUnits = json.series.units == 'ft³/s' ? 'cfs' : 'kcfs'
    let datastream = json.series.data
    for (let i = 0; i < datastream.length; i++) {
      let val

      // We do this for the values going onto the y0 axis chart
      val = Number.parseFloat(datastream[i].flow.toFixed(2))
      if (isMissing(val)) continue

      // Create the basic label.
      let validTime = datastream[i].validTime
      let valid = dayjs(validTime).valueOf()

      // Fake the data: always want CFS.
      // IF we have ratings we convert to a stage value else we don't have
      // primary and secondary data; only primary which is CFS.
      if (ratings) {
        // NOTE: we have to have ratingsLookup check on the CFS/KCFS conversion here.
        let converted = ratingsLookup(ratings, val, 'flow', flowUnits)
        datums.push({ x: valid, y: val, primary: converted, secondary: val })
      } else {
        datums.push({ x: valid, y: val, secondary: val })
      }
    }

    // Get min and max for this nwm series.
    if (datums.length) {
      // FAST!!!
      let yMinMax = findMinMax(
        datums.map(function (o) {
          return o.y
        })
      )
      let xMinMax = findMinMax(
        datums.map(function (o) {
          return o.x
        })
      )
      return { data: datums, ymin: yMinMax[0], ymax: yMinMax[1], xmin: xMinMax[0], xmax: xMinMax[1] }
    }
  } catch (err) {
    console.error(`ERROR getNwmData with ${type} for chart ${series.version.chart}`, err)
  }
  return false
}

/**
 * Get forecast ensemble data.
 * @param {*} datastream
 * @param {*} ratings
 * @returns array of objects
 */
export function getForecastEnsembleData(datastream, ratings) {
  let values = []
  for (let i = 0; i < datastream.data.length; i++) {
    // We do this for the values going onto the y0 axis chart
    let val = Number.parseFloat(datastream.data[i].flow.toFixed(2))
    if (isMissing(val)) continue

    // Create the basic label.
    let valid = dayjs(datastream.data[i].validTime).valueOf()

    if (ratings) {
      let converted = ratingsLookup(ratings, val, 'flow')
      values.push({ x: valid, y: val, primary: converted, secondary: val })
    } else {
      values.push({ x: valid, y: val, secondary: val })
    }
  }
  return values
}

/**
 *
 * @param object series
 * @param string streamflow
 * @param JSON json
 * @param object ratings
 * @returns null
 */
export function transformAllStreamFlows(series, streamflow, json) {
  if (json && JSON.stringify(json) !== '{}' && streamflow === 'reach') {
    // series['reachId'] = json[streamflow].reachId // Not working (reachId doesn't exist there.)
    series['reachId'] = json.reachId // Working
  }
  if (json.series) {
    // This checks type of flow data from the NWM streamflow.
    let values = getNwmData(json, series.ratings)
    if (values && Object.keys(values).length) {
      series.nwm[streamflow] = {}
      series.nwm[streamflow]['units'] = 'cfs'
      series.nwm[streamflow]['referenceTime'] = json.series.referenceTime
      series.nwm[streamflow]['unit'] = json.series.units
      series.nwm[streamflow]['unitString'] = json.series.units !== 'ft' ? 'cfs' : 'ft'

      series.nwm[streamflow]['ymin'] = values.ymin
      series.nwm[streamflow]['ymax'] = values.ymax
      series.nwm[streamflow]['xmin'] = values.xmin
      series.nwm[streamflow]['xmax'] = values.xmax
      series.nwm[streamflow]['data'] = values.data
    }
  } else {
    // Forecast ensemble data
    if (streamflow !== 'reach') {
      for (let key in json) {
        if (json.hasOwnProperty(key) && json[key].referenceTime !== null) {
          let abkey = `${streamflow}_ensemble_${key}`
          let values = getForecastEnsembleData(json[key], series.ratings)

          if (values && Object.keys(values).length) {
            series.nwm[abkey] = {}
            series.nwm[abkey]['units'] = 'cfs'
            series.nwm[abkey]['referenceTime'] = json[key].referenceTime

            series.nwm[abkey]['xmin'] = Math.min.apply(
              null,
              values.map((i) => i.x)
            )
            series.nwm[abkey]['xmax'] = Math.max.apply(
              null,
              values.map((i) => i.x)
            )
            series.nwm[abkey]['ymin'] = Math.min.apply(
              null,
              values.map((i) => i.y)
            )
            series.nwm[abkey]['ymax'] = Math.max.apply(
              null,
              values.map((i) => i.y)
            )
            series.nwm[abkey]['data'] = values
          }
        }
      }
    }
  }
}

/**
 * Create the series rawData object
 * from alternative ratings values.
 * @param {*} val
 * @param {*} from
 * @param {*} to
 * @param {*} ratings
 * @returns empty array | object
 */
export function getWholesRawDataUpdateNWM(val, from, to, ratings = []) {
  if (!val) return []
  if (!ratings) return { secondary: sec, type: 'whole' }
  let sec = getRatingForStage(ratings, val, from, to)
  if (sec && !isNaN(sec) && sec != undefined && isFinite(sec)) {
    return { primary: val, secondary: sec, type: 'whole' }
  }
  return []
}

/**
 * Super nifty sort for series.rawData.
 * It sorts secondary first and if secondary values
 * match then it sorts by primary.
 * This function is specific to NWM hydrograph.
 * @param {*} array of objects
 * @returns float
 */
export function sortRawData(arr) {
  return arr.sort(function (a, b) {
    if (arr.hasOwnProperty('primary') && a.secondary === b.secondary) {
      return a.primary - b.primary
    }
    return a.secondary - b.secondary
  })
}
